import { QCBadge } from '@/components/UI';
import { PLACEHOLDER_VALUE } from '@/constants';
import type { QCStatus } from '@/types';
import type { ColDef } from 'ag-grid-enterprise';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../styles/components/table.module.css';

import type { Sample } from './types';

function qcCellRenderer({ value }: { value?: QCStatus | null }) {
  if (!value) return null;
  return <QCBadge qc={value} />;
}

export function buildRunTableColDefs(t: (key: string) => string): ColDef<Sample>[] {
  return [
    { field: 'flow_cell', headerName: t('col_flowcell'), maxWidth: 100, width: 100 },
    { field: 'lane', headerName: t('col_lane'), maxWidth: 80, width: 80 },
    { field: 'well_position', headerName: t('col_well'), maxWidth: 90, width: 90 },
    { field: 'demux_index', headerName: t('col_demux_id'), maxWidth: 100, width: 100 },
    { field: 'name', headerName: t('col_sample'), minWidth: 150 },
    { field: 'library_group', headerName: t('col_library_group'), minWidth: 120 },
    {
      field: 'sample_quality_qc',
      headerName: t('col_sample_quality_qc'),
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: qcCellRenderer,
      maxWidth: 160,
    },
    {
      field: 'decode_qc',
      headerName: t('col_decode_qc'),
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: qcCellRenderer,
      maxWidth: 120,
    },
    {
      field: 'lane_qc',
      headerName: t('col_lane_qc'),
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: qcCellRenderer,
      maxWidth: 110,
    },
    {
      field: 'controls_qc',
      headerName: t('col_controls_qc'),
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: qcCellRenderer,
      maxWidth: 120,
    },
    { field: 'warning', headerName: t('col_warning'), maxWidth: 110 },
    { field: 'sample_group', headerName: t('col_sample_group'), minWidth: 130 },
    { field: 'study_name', headerName: t('col_study'), minWidth: 130 },
    { field: 'org_name', headerName: t('col_organization'), minWidth: 150 },
  ] as ColDef<Sample>[];
}

export function useRunTableColDefs(): ColDef<Sample>[] {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  return useMemo(() => buildRunTableColDefs(t as (key: string) => string), [t]);
}

export type SampleType = {
  name: string;
  study_name: string;
  org_name: string;
  well_position: string;
  demux_index: number;
};

const isValidString = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '' && v !== PLACEHOLDER_VALUE;

const isValidDemuxIndex = (v: unknown): boolean => typeof v === 'number' && Number.isInteger(v) && v >= 0;

export function areAllSampleFieldsFilled(samples: SampleType[]): boolean {
  if (!Array.isArray(samples) || samples.length === 0) return false;

  return samples.every(
    sample =>
      isValidString(sample.name) &&
      isValidString(sample.study_name) &&
      isValidString(sample.org_name) &&
      isValidString(sample.well_position) &&
      isValidDemuxIndex(sample.demux_index),
  );
}

export function isSampleCountCorrect(samples: ReadonlyArray<unknown>, expectedCount: number): boolean {
  if (!Array.isArray(samples)) return false;
  return samples.length === expectedCount;
}
